import React, { useState, useRef, useEffect, useCallback } from 'react';
import '../homes.css';
import dahboard from '../../../assets/images/darhboard.png';
import MenuItemCard from './menuItemCard';
import './menuItemcard.css';
import MenuItemTable from './menuItemTable';
import AddItembox from './addItems/addItembox';
import ApiService from '../../../services/apiService';
import CategoryNavBar from './categoryNav/categoryNav';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router';
import { ReactComponent as Items } from '../../../assets/images/svgIcons/menu.svg';
import MenuGrid from './grid/menuGrid';

const Menu = () => {
  const location = useLocation();
  const [menu, setMenu] = useState([]);
  const [view, setView] = useState('card');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const scrollContainerRef = useRef(null);
  const pageSize = 25;

  // Search function with debouncing
  const performSearch = useCallback((searchValue, pageNumber = 1, isNewSearch = false) => {
    GetMenu(pageNumber, isNewSearch, searchValue);
  }, []);

  // Handle search input change with debouncing
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set loading state for search
    setIsSearching(true);

    // Create new timeout for debounced search
    const newTimeout = setTimeout(() => {
      setCurrentPage(1);
      setMenu([]);
      setHasMoreData(true);
      performSearch(value, 1, true);
      setIsSearching(false);
    }, 500); // 500ms debounce delay

    setSearchTimeout(newTimeout);
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
    setMenu([]);
    setHasMoreData(true);
    GetMenu(1, true, '');
  };

  useEffect(() => {
    // Only fetch initial data if not coming from category
    if (!location.state || !location.state.categorymenu) {
      setCurrentPage(1);
      setMenu([]);
      setHasMoreData(true);
      GetMenu(1, true, searchTerm);
    }
  }, []);

  useEffect(() => {
    if (location.state && location.state.categorymenu) {
      setMenu(location.state.categorymenu);
      setHasMoreData(false); // Disable infinite scroll when showing category data
      setSearchTerm(''); // Clear search when showing category data
    }
  }, [location]);

  const GetMenu = async (pageNumber = 1, isNewSearch = false, searchValue = null) => {
    if (loading) return; // Prevent multiple simultaneous requests

    try {
      setLoading(true);
      const apiService = new ApiService();

      // Use passed searchValue or current searchTerm
      const currentSearchTerm = searchValue !== null ? searchValue : searchTerm;

      let parameters = `?itemName=${currentSearchTerm}&pageNumber=${pageNumber}&pageSize=${pageSize}`;
      const res = await apiService.getApiParamater('GET_ITEM_QUICK', parameters);

      if (res?.data?.statusCode === 200 || res?.data?.statusCode === 201) {
        const newItems = res.data.result || [];
        const total = res.data.totalCount || 0;

        setTotalItems(total);

        if (isNewSearch) {
          // Replace existing data for new search
          setMenu(newItems);
        } else {
          // Append new items for pagination
          setMenu((prevMenu) => [...prevMenu, ...newItems]);
        }

        // Check if there are more items to load
        setHasMoreData(newItems.length === pageSize);
      } else if (res?.data?.statusCode === 204) {
        if (isNewSearch) {
          setMenu([]);
          setTotalItems(0);
        }
        setHasMoreData(false);
      }
    } catch (error) {
      console.error('Error fetching menu data:', error);
      setHasMoreData(false);
    } finally {
      setLoading(false);
    }
  };

  // Load more items when scrolling
  const loadMoreItems = useCallback(() => {
    if (hasMoreData && !loading && !location.state?.categorymenu) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      GetMenu(nextPage, false);
    }
  }, [currentPage, hasMoreData, loading, searchTerm, location.state]);

  // Scroll event handler
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || !hasMoreData || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const threshold = 100; // Load more when 100px from bottom

    if (scrollTop + clientHeight >= scrollHeight - threshold) {
      loadMoreItems();
    }
  }, [hasMoreData, loading, loadMoreItems]);

  // Add scroll listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const toggleView = () => {
    setView(view === 'card' ? 'table' : 'card');
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  return (
    <div className="container-fluid p-lg-5 m-0">
      <div className="pt-3">
        <div className="row">
          <div className="col-lg-6 col-md-6 mbl-center">
            <div className="d-flex gap-4 pt-3 align-items-center">
              <div className="position-relative w-50">
                <input
                  type="search"
                  className="form-control pr-5"
                  placeholder="Search items by name..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  style={{ paddingRight: searchTerm ? '40px' : '16px' }}
                />
                {searchTerm && (
                  <button
                    className="btn btn-link position-absolute"
                    onClick={clearSearch}
                    style={{
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      padding: '4px 8px',
                      color: '#6c757d',
                      textDecoration: 'none',
                      zIndex: 10,
                    }}
                    title="Clear search"
                  >
                    ✕
                  </button>
                )}
                {isSearching && (
                  <div
                    className="position-absolute"
                    style={{
                      right: searchTerm ? '40px' : '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                    }}
                  >
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Searching...</span>
                    </div>
                  </div>
                )}
              </div>
              <img onClick={toggleView} src={dahboard} className="filter" style={{ cursor: 'pointer' }} alt="Toggle View" />
            </div>
          </div>

          <div className="col-lg-6 col-md-6">
            <div className="d-flex justify-content-end pt-top mbl-center">
              <AddItembox />
            </div>
          </div>
        </div>

        <div>
          <div className="menu">
            {/* Only show CategoryNavBar if not searching */}
            {!searchTerm && (
              <div className="pt-5">
                <CategoryNavBar />
              </div>
            )}

            {/* Search indicator */}
            {searchTerm && (
              <div className="pt-3 mb-3">
                <div className="alert alert-info" role="alert">
                  <strong>Searching for:</strong> "{searchTerm}"
                  {totalItems > 0 && (
                    <span className="ms-2">
                      • Found {totalItems} item{totalItems !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Results counter */}
            {menu.length > 0 && (
              <div className="pt-3">
                <small className="text-muted">
                  Showing {menu.length} {totalItems > 0 && `of ${totalItems}`} items
                  {loading && ' (Loading more...)'}
                </small>
              </div>
            )}

            <div
              className="menu-items pt-5"
              ref={scrollContainerRef}
              style={{
                maxHeight: view === 'card' ? '80vh' : 'auto',
                overflowY: view === 'card' ? 'auto' : 'visible',
              }}
            >
              {view === 'table' && menu?.length > 0 && (
                <div className="table-responsive">
                  <MenuGrid val={menu} GetMenu={() => GetMenu(1, true, searchTerm)} />
                </div>
              )}

              {menu?.length > 0 ? (
                <div className="grid">
                  {view === 'card' &&
                    menu.map((item, itemIndex) => (
                      <MenuItemCard key={`${item.id || item.record_Id}-${itemIndex}`} {...item} item={item} GetMenu={() => GetMenu(1, true, searchTerm)} />
                    ))}
                </div>
              ) : (
                <div className="center p-lg-5">
                  {loading || isSearching ? (
                    <div className="text-center">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <h5 className="mt-3">{searchTerm ? 'Searching...' : 'Loading items...'}</h5>
                    </div>
                  ) : (
                    <>
                      {searchTerm ? (
                        <div className="text-center">
                          <h5>No items found for "{searchTerm}"</h5>
                          <p className="text-muted mt-2">Try searching with different keywords</p>
                          <button className="btn btn-primary mt-3" onClick={clearSearch}>
                            Clear Search
                          </button>
                        </div>
                      ) : (
                        <h5>No Items Available</h5>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Loading indicator for infinite scroll */}
              {loading && menu.length > 0 && !isSearching && (
                <div className="text-center py-4">
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Loading more...</span>
                  </div>
                  <p className="mt-2 text-muted">Loading more items...</p>
                </div>
              )}

              {/* End of results indicator */}
              {!hasMoreData && menu.length > 0 && !loading && (
                <div className="text-center py-4">
                  <p className="text-muted">{searchTerm ? 'No more search results' : "You've reached the end of the list"}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Menu;
